import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/TemplateManagement.scss';
import TemplateDesigner from '@/components/ActionItems/TemplateDesigner';
import PageHeader from '@/components/Common/PageHeader';
import { FaEdit, FaSpinner, FaLayerGroup, FaPlus, FaInbox, FaFolder, FaPause, FaPlay, FaMagic, FaSearch, FaTimes } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Template Management Page - Admin interface for managing Action Item templates
 * Allows creating, editing, activating/deactivating templates for different departments
 * Includes a visual Template Designer for creating and editing template schemas
 */
const TemplateManagementPage = () => {
    const { apiFetch, user } = useAuth();
    const navigate = useNavigate();
    const [groupedTemplates, setGroupedTemplates] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showDesigner, setShowDesigner] = useState(false);
    const [filterDepartment, setFilterDepartment] = useState('All');
    const [showInactive, setShowInactive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Default departments as fallback when API is unavailable
    const DEFAULT_DEPARTMENTS = ['Operations', 'IT', 'Legal', 'Finance', 'Leasing', 'Marketing', 'SIPA'];
    
    // Departments fetched from API - used for filter dropdown
    const [departments, setDepartments] = useState(['All', ...DEFAULT_DEPARTMENTS]);
    // Department names only (without 'All') - used for TemplateDesigner dropdown
    const [departmentNames, setDepartmentNames] = useState(DEFAULT_DEPARTMENTS);
    const [, setDepartmentsLoading] = useState(true);

    // Get current user ID from user context
    const userId = user?.userID || parseInt(localStorage.getItem('userID') || '0', 10);

    /**
     * Fetch departments from the API
     * Sets both the filter dropdown (with 'All') and designer dropdown (names only)
     * Falls back to DEFAULT_DEPARTMENTS if API fails
     */
    const fetchDepartments = useCallback(async () => {
        try {
            setDepartmentsLoading(true);
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            
            if (data.success && data.data && data.data.length > 0) {
                // Extract department names for the filter and designer dropdowns
                const deptNames = data.data.map(d => d.departmentName);
                setDepartments(['All', ...deptNames]); // For filter dropdown
                setDepartmentNames(deptNames); // For TemplateDesigner dropdown
            } else {
                // Fallback to defaults if API returns empty
                setDepartments(['All', ...DEFAULT_DEPARTMENTS]);
                setDepartmentNames(DEFAULT_DEPARTMENTS);
                console.warn('No departments from API, using defaults');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading departments:', error);
            // Fallback to defaults on error
            setDepartments(['All', ...DEFAULT_DEPARTMENTS]);
            setDepartmentNames(DEFAULT_DEPARTMENTS);
        } finally {
            setDepartmentsLoading(false);
        }
    }, [apiFetch]);

    /**
     * Group templates by department for organized display
     * @param {Array} templateList - List of templates to group
     */
    const groupTemplatesByDepartment = useCallback((templateList) => {
        const grouped = templateList.reduce((acc, template) => {
            const dept = template.department || 'Other';
            if (!acc[dept]) {
                acc[dept] = [];
            }
            acc[dept].push(template);
            return acc;
        }, {});

        // Sort templates within each department by sortOrder
        Object.keys(grouped).forEach(dept => {
            grouped[dept].sort((a, b) => a.sortOrder - b.sortOrder);
        });

        setGroupedTemplates(grouped);
    }, []);

    /**
     * Load all templates from the API
     */
    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/templates?includeInactive=${showInactive}`);
            const data = await response.json();

            if (data.success) {
                groupTemplatesByDepartment(data.data);
            } else {
                toast.error('Failed to load templates');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading templates:', error);
            toast.error('Error loading templates: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [showInactive, groupTemplatesByDepartment, apiFetch]);

    useEffect(() => {
        fetchDepartments();
        loadTemplates();
    }, [fetchDepartments, loadTemplates]);

    useEffect(() => {
        if (showDesigner) {
            document.body.classList.add('designer-open');
        } else {
            document.body.classList.remove('designer-open');
        }
        return () => document.body.classList.remove('designer-open');
    }, [showDesigner]);

    /**
     * Filter templates based on selected department and free-text search.
     * Search matches displayName, templateKey, description, and tags so admins
     * can locate a template by any known attribute.
     */
    const getFilteredTemplates = () => {
        const q = searchQuery.trim().toLowerCase();
        const matches = (tpl) => {
            if (!q) return true;
            const haystack = [
                tpl.displayName,
                tpl.templateKey,
                tpl.description,
                tpl.tags,
                tpl.department
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        };

        const base = filterDepartment === 'All'
            ? groupedTemplates
            : { [filterDepartment]: groupedTemplates[filterDepartment] || [] };

        if (!q) return base;

        const out = {};
        for (const [dept, tpls] of Object.entries(base)) {
            const filtered = tpls.filter(matches);
            if (filtered.length) out[dept] = filtered;
        }
        return out;
    };

    const totalTemplateCount = useMemo(
        () => Object.values(groupedTemplates).reduce((n, list) => n + list.length, 0),
        [groupedTemplates]
    );

    /**
     * Handle template activation/deactivation
     */
    const handleToggleActive = async (templateKey, currentStatus) => {
        try {
            const endpoint = currentStatus 
                ? `/api/templates/${templateKey}?userId=${userId}`
                : `/api/templates/${templateKey}/reactivate?userId=${userId}`;
            
            const response = currentStatus 
                ? await apiFetch(endpoint, { method: 'DELETE' })
                : await apiFetch(endpoint, { method: 'POST' });
            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                loadTemplates();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling template status:', error);
            toast.error('Failed to update template status');
        }
    };

    /**
     * Handle template edit - opens the Template Designer with the selected template
     */
    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowDesigner(true);
    };

    /**
     * Handle creating new template - opens the Template Designer with no initial template
     */
    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setShowDesigner(true);
    };

    /**
     * Handle closing the Template Designer
     */
    const handleCloseDesigner = () => {
        setShowDesigner(false);
        setSelectedTemplate(null);
    };

    /**
     * Handle opening the Template Wizard - closes designer and navigates
     */
    const handleOpenWizard = () => {
        handleCloseDesigner();
        navigate('/settings/template-wizard');
    };

    /**
     * Handle saving a template from the Designer
     * @param {object} templateData - The template data to save
     * @param {string|null} existingKey - The existing template key if editing
     */
    const handleSaveTemplate = async (templateData, existingKey = null) => {
        try {
            let response;
            
            if (existingKey) {
                // Update existing template
                response = await apiFetch(`/api/templates/${existingKey}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(templateData)
                });
            } else {
                // Create new template
                response = await apiFetch('/api/templates', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(templateData)
                });
            }

            const data = await response.json();
            if (data.success) {
                toast.success(data.message || (existingKey ? 'Template updated successfully' : 'Template created successfully'));
                loadTemplates();
                handleCloseDesigner();
            } else {
                toast.error(data.message || 'Failed to save template');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving template:', error);
            toast.error('Failed to save template');
            throw error; // Re-throw to let designer handle it
        }
    };

    /**
     * Render template card
     */
    const renderTemplateCard = (template) => {
        return (
            <div 
                key={template.templateId} 
                className={`template-card ${!template.isActive ? 'inactive' : ''}`}
                style={{ borderLeftColor: template.color || '#6c757d' }}
            >
                <div className="template-header">
                    <div className="template-icon" style={{ color: template.color || '#6c757d' }}>
                        <i className={`fas ${template.icon || 'fa-file'}`}></i>
                    </div>
                    <div className="template-title">
                        <h4>{template.displayName}</h4>
                        <span className="template-key">{template.templateKey}</span>
                    </div>
                    <div className="template-status">
                        {template.isSystemTemplate && (
                            <span className="badge badge-info">System</span>
                        )}
                        {!template.isActive && (
                            <span className="badge badge-secondary">Inactive</span>
                        )}
                    </div>
                </div>

                <div className="template-body">
                    <p className="template-description">{template.description}</p>
                    
                    <div className="template-meta">
                        <div className="meta-item">
                            <strong>Default Priority:</strong> {template.defaultPriority}
                        </div>
                        <div className="meta-item">
                            <strong>Default Status:</strong> {template.defaultStatus}
                        </div>
                        {template.defaultDueDays && (
                            <div className="meta-item">
                                <strong>Default Due:</strong> {template.defaultDueDays} days
                            </div>
                        )}
                    </div>

                    {template.tags && (
                        <div className="template-tags">
                            {template.tags.split(',').map((tag, idx) => (
                                <span key={idx} className="tag">{tag.trim()}</span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="template-actions">
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(template)}
                    >
                        <FaEdit /> Edit
                    </button>
                    
                    <button 
                        className={`btn btn-sm ${template.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                        onClick={() => handleToggleActive(template.templateKey, template.isActive)}
                    >
                        {template.isActive ? <FaPause /> : <FaPlay />}
                        {template.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="template-management-page">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin fa-3x" />
                    <p>Loading templates...</p>
                </div>
            </div>
        );
    }

    const filteredTemplates = getFilteredTemplates();

    return (
        <div className="template-management-page">
            <PageHeader
                icon={<FaLayerGroup />}
                title="Action Item Template Designer"
                description={<>Step 1 of 3: design the form fields for each action item type. After creating templates here, assign them to modules in <strong>Module Assignments</strong> (Step 2), then configure visibility in <strong>Board Creation Rules</strong> (Step 3).</>}
                meta={(
                    <>
                        <span className="ctx-page-header-pill tone-info">
                            {totalTemplateCount} template{totalTemplateCount === 1 ? '' : 's'}
                        </span>
                        {showInactive && (
                            <span className="ctx-page-header-pill tone-warn">Including inactive</span>
                        )}
                    </>
                )}
                secondaryActions={(
                    <button
                        className="btn btn-wizard"
                        onClick={() => navigate('/settings/template-wizard')}
                        title="Create a template from a pre-built module preset"
                    >
                        <FaMagic /> Template Wizard
                    </button>
                )}
                primaryAction={(
                    <button className="btn btn-primary" onClick={handleCreateNew}>
                        <FaPlus /> Create New Template
                    </button>
                )}
                toolbar={(
                    <>
                        <div className="tpl-search" role="search">
                            <FaSearch className="tpl-search-icon" aria-hidden="true" />
                            <input
                                type="search"
                                className="form-control tpl-search-input"
                                placeholder="Search by name, key, description, or tag..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search templates"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    className="tpl-search-clear"
                                    aria-label="Clear search"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        <div className="form-group mb-0">
                            <label htmlFor="departmentFilter" className="sr-only">Department</label>
                            <select
                                id="departmentFilter"
                                className="form-control"
                                value={filterDepartment}
                                onChange={(e) => setFilterDepartment(e.target.value)}
                            >
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>
                                        {dept === 'All' ? 'All departments' : dept}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <label className="tpl-inline-check">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                            Show inactive
                        </label>
                    </>
                )}
            />

            <div className="templates-container">
                {Object.keys(filteredTemplates).length === 0 ? (
                    <div className="no-templates">
                        <FaInbox className="fa-3x" />
                        {searchQuery ? (
                            <>
                                <p>No templates match <strong>&ldquo;{searchQuery}&rdquo;</strong>.</p>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setSearchQuery('')}>
                                    Clear search
                                </button>
                            </>
                        ) : filterDepartment !== 'All' ? (
                            <>
                                <p>No templates in the <strong>{filterDepartment}</strong> department yet.</p>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNew}>
                                    <FaPlus /> Create one now
                                </button>
                            </>
                        ) : (
                            <>
                                <p>No templates have been created yet.</p>
                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                    <button type="button" className="btn btn-wizard" onClick={() => navigate('/settings/template-wizard')}>
                                        <FaMagic /> Start with a preset
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleCreateNew}>
                                        <FaPlus /> Create from scratch
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    Object.keys(filteredTemplates).sort().map(department => (
                        <div key={department} className="department-section">
                            <h2 className="department-heading">
                                <FaFolder /> {department}
                                <span className="template-count">
                                    ({filteredTemplates[department].length} template{filteredTemplates[department].length !== 1 ? 's' : ''})
                                </span>
                            </h2>
                            <div className="templates-grid">
                                {filteredTemplates[department].map(template => renderTemplateCard(template))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Template Designer Modal - Full-featured template editor */}
            {showDesigner && (
                <div className="modal-backdrop designer-modal-backdrop" onClick={handleCloseDesigner}>
                    <div className="modal-content designer-modal-content" onClick={(e) => e.stopPropagation()}>
                        <TemplateDesigner
                            initialTemplate={selectedTemplate}
                            onSave={handleSaveTemplate}
                            onCancel={handleCloseDesigner}
                            userId={userId}
                            departments={departmentNames}
                            onOpenWizard={handleOpenWizard}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateManagementPage;
